import { useEffect, useRef, useState } from "react"
import styles from "./GapSyncPanel.module.css"
import {
  type GapListItem,
  type GapListKind,
  type YutaiGapReport,
  useGapSync,
} from "./useGapSync.js"

interface GapSyncPanelProps {
  onMessage: (message: string) => void
}

const LIST_LABELS: Record<GapListKind, string> = {
  "no-url": "他サイト優待あり・URL未取得",
  "not-crawled": "他サイト優待あり・URL取得済み・未クロール",
}

export function GapSyncPanel({ onMessage }: GapSyncPanelProps) {
  const {
    report,
    loading,
    listLoading,
    error,
    mode,
    setMode,
    activeListKind,
    listPage,
    loadReport,
    fetchGapList,
    toggleGapList,
    GAP_LIST_PAGE_SIZE,
  } = useGapSync()
  const [open, setOpen] = useState(false)
  const countsLoadedRef = useRef(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: reload counts when comparison mode changes
  useEffect(() => {
    countsLoadedRef.current = false
  }, [mode])

  useEffect(() => {
    if (!open || countsLoadedRef.current) return
    countsLoadedRef.current = true
    void loadReport().catch(() => {
      countsLoadedRef.current = false
    })
  }, [open, loadReport])

  async function handleRefreshCounts() {
    try {
      await loadReport()
      if (activeListKind && listPage) {
        await fetchGapList(activeListKind, listPage.page)
      }
      onMessage("✓ 件数を更新しました")
    } catch (e) {
      onMessage(`✗ 件数取得エラー: ${e}`)
    }
  }

  async function handleToggleList(kind: GapListKind) {
    try {
      await toggleGapList(kind)
    } catch (e) {
      onMessage(`✗ 一覧取得エラー: ${e}`)
    }
  }

  async function handleListPageChange(page: number) {
    if (!activeListKind) return
    try {
      await fetchGapList(activeListKind, page)
    } catch (e) {
      onMessage(`✗ 一覧取得エラー: ${e}`)
    }
  }

  const noUrlCount = report?.missingWithoutUrl
  const notCrawledCount = report?.missingWithUrlNotCrawled

  return (
    <section className={styles.section}>
      <button type="button" className={styles.toggle} onClick={() => setOpen((v) => !v)}>
        {open ? "▾" : "▸"} 他サイト優待の漏れ
      </button>

      {open && (
        <div className={styles.panel}>
          <p className={styles.lead}>
            他サイトに優待があるのに当サイト未公開の銘柄を、URL取得状況ごとに一覧表示します。
            URL未取得の銘柄は3分ごとのcronで1件ずつ自動取得します（実行ログの「漏れURL取得」で確認）。
          </p>

          <div className={styles.controlGroup}>
            <span className={styles.controlLabel}>比較の広さ</span>
            <div className={styles.segmented}>
              <button
                type="button"
                className={`${styles.segmentBtn} ${mode === "broad" ? styles.segmentBtnActive : ""}`}
                onClick={() => setMode("broad")}
                disabled={listLoading}
              >
                広め
              </button>
              <button
                type="button"
                className={`${styles.segmentBtn} ${mode === "conservative" ? styles.segmentBtnActive : ""}`}
                onClick={() => setMode("conservative")}
                disabled={listLoading}
              >
                確認済みのみ
              </button>
            </div>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={handleRefreshCounts}
              disabled={loading || listLoading}
            >
              {loading ? "更新中..." : "件数を更新"}
            </button>
          </div>

          {error && <div className={styles.error}>{error}</div>}

          {report && <GapReportSummary report={report} />}

          <div className={styles.listActions}>
            <button
              type="button"
              className={`${styles.listActionBtn} ${activeListKind === "no-url" ? styles.listActionBtnActive : ""}`}
              onClick={() => handleToggleList("no-url")}
              disabled={loading || listLoading}
            >
              {LIST_LABELS["no-url"]}
              {noUrlCount !== undefined && `（${noUrlCount.toLocaleString()}件）`}
            </button>
            <button
              type="button"
              className={`${styles.listActionBtn} ${activeListKind === "not-crawled" ? styles.listActionBtnActive : ""}`}
              onClick={() => handleToggleList("not-crawled")}
              disabled={loading || listLoading}
            >
              {LIST_LABELS["not-crawled"]}
              {notCrawledCount !== undefined && `（${notCrawledCount.toLocaleString()}件）`}
            </button>
          </div>

          {listLoading && <div className={styles.listLoading}>一覧を読み込み中...</div>}

          {activeListKind && listPage && !listLoading && (
            <GapListView
              kind={activeListKind}
              page={listPage}
              pageSize={GAP_LIST_PAGE_SIZE}
              onPageChange={handleListPageChange}
            />
          )}

          <p className={styles.hint}>
            「外部優待URL不可」になった銘柄はcronでは再試行しません。上部の統計カードから絞り込み、手動でURL入力または「候補を見る」で対応してください。
          </p>
        </div>
      )}
    </section>
  )
}

function GapReportSummary({ report }: { report: YutaiGapReport }) {
  return (
    <div className={styles.summary}>
      <div className={styles.summaryItem}>
        <span className={styles.summaryNum}>{report.missing.length.toLocaleString()}</span>
        <span className={styles.summaryLabel}>漏れ銘柄</span>
      </div>
      <div
        className={`${styles.summaryItem} ${report.missingWithoutUrl > 0 ? styles.summaryWarn : ""}`}
      >
        <span className={styles.summaryNum}>{report.missingWithoutUrl.toLocaleString()}</span>
        <span className={styles.summaryLabel}>うちURL未取得</span>
      </div>
      <div
        className={`${styles.summaryItem} ${report.missingWithUrlNotCrawled > 0 ? styles.summaryWarn : ""}`}
      >
        <span className={styles.summaryNum}>
          {report.missingWithUrlNotCrawled.toLocaleString()}
        </span>
        <span className={styles.summaryLabel}>うち未クロール</span>
      </div>
      <div
        className={`${styles.summaryItem} ${report.externalBenefitUrlFailed > 0 ? styles.summaryWarn : ""}`}
      >
        <span className={styles.summaryNum}>
          {report.externalBenefitUrlFailed.toLocaleString()}
        </span>
        <span className={styles.summaryLabel}>外部優待URL不可</span>
      </div>
    </div>
  )
}

function GapListView({
  kind,
  page,
  pageSize,
  onPageChange,
}: {
  kind: GapListKind
  page: {
    items: GapListItem[]
    total: number
    page: number
    totalPages: number
  }
  pageSize: number
  onPageChange: (page: number) => void
}) {
  const start = page.total === 0 ? 0 : (page.page - 1) * pageSize + 1
  const end = Math.min(page.page * pageSize, page.total)

  return (
    <div className={styles.gapList}>
      <div className={styles.gapListHeader}>
        <span className={styles.gapListTitle}>{LIST_LABELS[kind]}</span>
        <span className={styles.gapListMeta}>
          {page.total === 0
            ? "0件"
            : `${start.toLocaleString()}–${end.toLocaleString()} / ${page.total.toLocaleString()}件`}
        </span>
      </div>

      {page.items.length === 0 ? (
        <p className={styles.gapListEmpty}>該当する銘柄はありません。</p>
      ) : (
        <ul className={styles.gapListItems}>
          {page.items.map((item) => (
            <li key={item.id} className={styles.gapListItem}>
              <div className={styles.gapListItemMain}>
                <span className={styles.gapListCode}>{item.id}</span>
                <span className={styles.gapListName}>{item.name}</span>
              </div>
              {kind === "no-url" && item.externalBenefitUrlFailedAt && (
                <span className={styles.gapListBadgeWarn}>外部優待URL不可</span>
              )}
              {kind === "not-crawled" && item.officialUrl && (
                <a
                  className={styles.gapListUrl}
                  href={item.officialUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {item.officialUrl}
                </a>
              )}
              {kind === "not-crawled" && item.crawlStatus && (
                <span className={styles.gapListStatus}>クロール: {item.crawlStatus}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {page.totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageBtn}
            onClick={() => onPageChange(page.page - 1)}
            disabled={page.page <= 1}
          >
            前へ
          </button>
          <span className={styles.pageInfo}>
            {page.page} / {page.totalPages}
          </span>
          <button
            type="button"
            className={styles.pageBtn}
            onClick={() => onPageChange(page.page + 1)}
            disabled={page.page >= page.totalPages}
          >
            次へ
          </button>
        </div>
      )}
    </div>
  )
}
